import type { SiteInput } from '../../repos/sitesRepo';
import type {
  RefrigerationUnit,
  Site,
  VisitDetailedCheck,
  VisitDpiInventory,
  VisitInailCompliance,
  VisitUnitCheck,
} from '../../schemas';
import { newId } from '../../../utils/ids';

import {
  addMonths,
  formatControlPeriodRange,
  startOfMonth,
  toIsoDate,
  toIsoDateTime,
} from './dates';
import { pick, randomBool, randomInt, shuffle, type Rng } from './rng';
import { FIRST_NAMES, LAST_NAMES } from './people';
import { generateRefrigerationUnits } from './seedUnits';
import {
  ALARM_PANEL_STATES,
  DETAILED_CHECK_LIBRARY,
  DETAILED_VERIFIERS,
  DIALER_STATES,
  EXIT_ROUTE_STATES,
  FILTER_CAPACITIES,
  FILTER_TYPES,
  FILTER_VALENCES,
  FIRST_AID_STATES,
  LEAK_RESOLUTIONS,
  LEAK_SOURCES,
  SCBA_REGULATOR_TYPES,
  SELECTOR_STATES,
  SENSOR_STATUS,
  SHUTOFF_TYPES,
  SIGNAGE_STATES,
  TERNARY_COMPLIANCE,
  VALVE_CONTROL_STATES,
  ACTIVATION_METHODS,
} from './constants';
import { mostlyConforme, readinessStatus } from './compliance';
import type {
  EmergencyDrillSeed,
  GeneratedSite,
  InfrastructureCheckSeed,
  RegistersSeed,
  SafetySummarySeed,
  ScorecardSeedRow,
  SimulationSeed,
  SiteIssueRecord,
  TrainingUpdateSeed,
  VisitSeedData,
} from './types';

const CITY_OPTIONS = [
  { city: 'Milano', province: 'MI', region: 'Lombardia' },
  { city: 'Bergamo', province: 'BG', region: 'Lombardia' },
  { city: 'Parma', province: 'PR', region: 'Emilia-Romagna' },
  { city: 'Verona', province: 'VR', region: 'Veneto' },
  { city: 'Torino', province: 'TO', region: 'Piemonte' },
  { city: 'Bari', province: 'BA', region: 'Puglia' },
  { city: 'Roma', province: 'RM', region: 'Lazio' },
  { city: 'Napoli', province: 'NA', region: 'Campania' },
  { city: 'Firenze', province: 'FI', region: 'Toscana' },
  { city: 'Piacenza', province: 'PC', region: 'Emilia-Romagna' },
  { city: 'Padova', province: 'PD', region: 'Veneto' },
  { city: 'Ancona', province: 'AN', region: 'Marche' },
];

const SITE_PURPOSES = [
  'Hub logistico',
  'Stabilimento produzione',
  'Magazzino refrigerato',
  'Sala macchine',
  'Linea trattamento',
];

const WORKDAY_TIME_SLOTS = [
  { hour: 8, minute: 30 },
  { hour: 9, minute: 0 },
  { hour: 9, minute: 30 },
  { hour: 10, minute: 0 },
  { hour: 10, minute: 30 },
  { hour: 11, minute: 0 },
  { hour: 11, minute: 30 },
  { hour: 12, minute: 0 },
  { hour: 14, minute: 0 },
  { hour: 14, minute: 30 },
  { hour: 15, minute: 0 },
  { hour: 15, minute: 30 },
  { hour: 16, minute: 0 },
  { hour: 16, minute: 30 },
  { hour: 17, minute: 0 },
];

const applyWorkingHours = (date: Date, rng: Rng): Date => {
  const slotIndex = randomInt(rng, 0, WORKDAY_TIME_SLOTS.length - 1);
  const slot = WORKDAY_TIME_SLOTS[slotIndex]!;
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    slot.hour,
    slot.minute,
    0,
    0
  );
};

const DEFAULT_SITE_LOCATIONS = [
  'Sala macchine principale',
  'Area picking',
  'Magazzino bassa temperatura',
  'Reparto confezionamento',
  'Tetto tecnico',
];

const DEFAULT_GAS_TYPES = ['Ammoniaca', 'Ammoniaca anidra'];

const SITE_FREQUENCIES: Site['frequency'][] = ['four_monthly', 'quarterly', 'semiannual'];

const FREQUENCY_TO_MONTHS = new Map<Site['frequency'], number>([
  ['monthly', 1],
  ['bimonthly', 2],
  ['quarterly', 3],
  ['four_monthly', 4],
  ['semiannual', 6],
  ['annual', 12],
]);

const INFRASTRUCTURE_FIELDS = [
  { id: 'lighting-exit', label: 'Illuminazione esodo' },
  { id: 'lighting-intervention', label: 'Illuminazione intervento' },
  { id: 'door-closure', label: 'Chiusura porte' },
  { id: 'cctv', label: 'Videosorveglianza' },
  { id: 'intrusion', label: 'Allarme antintrusione' },
  { id: 'vvf-docs', label: 'Cassetta documenti VVF' },
] as const;

function buildVisitUnitChecks(
  units: RefrigerationUnit[],
  rng: Rng,
  referenceDate: Date
): VisitUnitCheck[] {
  return units
    .map((unit) => {
      const maybeUnsetBool = (value: boolean, chance = 0.2) => (rng() > chance ? value : undefined);
      const hasAbatementSystem = Boolean(unit.technical?.safety?.abatementSystem?.present);
      const leakDetected = randomBool(rng, 0.18);
      const leakReported = leakDetected || randomBool(rng, 0.25);
      const leakResolution = leakReported ? pick(rng, LEAK_RESOLUTIONS) : 'n_a';
      const extraordinaryWork = randomBool(rng, 0.35);
      const sensorsStatus = pick(rng, SENSOR_STATUS);
      const lastCalibration = toIsoDate(addMonths(referenceDate, -randomInt(rng, 4, 9)));
      const lastElementReplacement = toIsoDate(addMonths(referenceDate, -randomInt(rng, 10, 18)));
      const replacementSchedule = rng() > 0.6 ? 'Annuale' : 'Biennale';
      const maintainer = rng() > 0.5 ? 'SafetyTech Italia' : 'Manutenzioni Verdi';
      const reliefPeriodYears = randomInt(rng, 1, 3);
      const nextDue = toIsoDate(addMonths(referenceDate, reliefPeriodYears * 12));
      const shutoffType = pick(rng, SHUTOFF_TYPES);
      const sealedOn = shutoffType !== 'non_presente' && randomBool(rng, 0.7);
      const sealedNonOn = shutoffType === 'deviatore' ? randomBool(rng, 0.55) : false;
      const otherAlarmIssues = maybeUnsetBool(randomBool(rng, 0.2));

      return {
        id: newId(),
        unitId: unit.id,
        unitName: unit.name,
        refrigeration: {
          leaksPresent: leakDetected,
          leaksReported: leakReported,
          leakSource: leakReported ? pick(rng, LEAK_SOURCES) : undefined,
          leakResolution,
          leakInjuries: leakDetected ? randomBool(rng, 0.05) : false,
          leakNotes:
            leakDetected || leakReported ? 'Monitoraggio programmato con manutentore' : undefined,
          condensationPressure: mostlyConforme(rng),
          oilTemperature: mostlyConforme(rng),
          parametersNotes: randomBool(rng, 0.35) ? 'Valori entro range controllati' : undefined,
          ordinaryMaintenance: mostlyConforme(rng),
          extraordinaryWork,
          extraordinaryDetails: extraordinaryWork
            ? 'Sostituzione guarnizioni compressori'
            : undefined,
        },
        safety: {
          sensors: {
            status: sensorsStatus,
            lastCalibration,
            periodicityMonths: randomInt(rng, 6, 12),
            lastElementReplacement,
            replacementSchedule,
            maintainer,
          },
          reliefValves: {
            setpoint: `${randomInt(rng, 16, 20)}-${randomInt(rng, 21, 24)}`,
            nextDue,
            periodYears: reliefPeriodYears,
          },
          shutoffValves: {
            type: shutoffType,
            sealedON: maybeUnsetBool(sealedOn),
            sealedNonON: maybeUnsetBool(sealedNonOn),
            integrity: mostlyConforme(rng),
          },
          extractors: {
            selectorState: pick(rng, SELECTOR_STATES),
            testRun: maybeUnsetBool(randomBool(rng, 0.7)),
            activationMethod: pick(rng, ACTIVATION_METHODS),
          },
          scrubber: {
            extractorSelector: pick(rng, SELECTOR_STATES),
            extractorTest: maybeUnsetBool(randomBool(rng, 0.65)),
            pumpSelector: pick(rng, SELECTOR_STATES),
            pumpTest: maybeUnsetBool(randomBool(rng, 0.6)),
          },
          abatementSystem: hasAbatementSystem
            ? {
                present: true,
                selectorState: pick(rng, SELECTOR_STATES),
                testRun: maybeUnsetBool(randomBool(rng, 0.6)),
                activationMethod: pick(rng, ACTIVATION_METHODS),
                trim: pick(rng, TERNARY_COMPLIANCE),
                notes:
                  rng() > 0.6 ? 'Sequenza automatica verificata con esito conforme' : undefined,
              }
            : { present: false },
          emergencyShutoff: {
            button: mostlyConforme(rng),
            detectors: mostlyConforme(rng),
            trim: pick(rng, TERNARY_COMPLIANCE),
          },
          alarmPanel: {
            state: pick(rng, ALARM_PANEL_STATES),
            history: mostlyConforme(rng),
            detectorsDisabled: maybeUnsetBool(randomBool(rng, 0.15)),
            otherIssues: otherAlarmIssues,
            issueDetails: otherAlarmIssues ? 'Consigliare verifica relè di consenso' : undefined,
          },
          dialer: {
            state: pick(rng, DIALER_STATES),
            contacts: 'Control Room; Responsabile impianto',
          },
          condenserWaterConductivity: randomInt(rng, 25, 80),
          conductivityNotes: randomBool(rng, 0.4)
            ? 'Misura in linea conforme'
            : 'Strumento non disponibile',
          alarmButton: pick(rng, TERNARY_COMPLIANCE),
          audioVisualAlarm: mostlyConforme(rng),
          exitRoutes: pick(rng, EXIT_ROUTE_STATES),
          valveControls: pick(rng, VALVE_CONTROL_STATES),
          notes: randomBool(rng, 0.45) ? `Verifica ${unit.name}: controlli regolari` : undefined,
        },
      } satisfies VisitUnitCheck;
    })
    .sort((a, b) => a.unitName.localeCompare(b.unitName));
}

function buildVisitDetailedChecks(units: RefrigerationUnit[], rng: Rng): VisitDetailedCheck[] {
  const checks: VisitDetailedCheck[] = [];
  for (const unit of units) {
    const sample = shuffle(rng, DETAILED_CHECK_LIBRARY).slice(0, 2 + randomInt(rng, 0, 1));
    sample.forEach((item, index) => {
      const nonConforming = randomBool(rng, 0.2);
      const result = nonConforming
        ? pick(rng, ['non_conforme', 'non_verificabile'] as const)
        : 'conforme';
      const notes = nonConforming
        ? 'Richiesta verifica approfondita nel prossimo intervento'
        : index === 0 && rng() > 0.6
          ? 'Controllo effettuato con personale patentato'
          : undefined;
      checks.push({
        id: newId(),
        unitId: unit.id,
        unitName: unit.name,
        item,
        result,
        verifiedBy: pick(rng, DETAILED_VERIFIERS),
        notes,
      });
    });
  }
  return checks.sort((a, b) => {
    const byUnit = a.unitName.localeCompare(b.unitName);
    if (byUnit !== 0) return byUnit;
    return a.item.localeCompare(b.item);
  });
}

function buildVisitDpiInventory(rng: Rng, referenceDate: Date): VisitDpiInventory {
  const filterExpiry = toIsoDate(addMonths(referenceDate, randomInt(rng, 6, 18)));
  const lastService = toIsoDate(addMonths(referenceDate, -randomInt(rng, 3, 9)));
  const nextCylinderInspection = toIsoDate(addMonths(referenceDate, randomInt(rng, 6, 12)));
  const signageState = pick(rng, SIGNAGE_STATES);

  return {
    general: {
      completeness: mostlyConforme(rng),
      accessibility: mostlyConforme(rng),
      condition: mostlyConforme(rng),
      keysPresent: randomBool(rng, 0.85),
      maintainer: rng() > 0.5 ? 'SafetyTech Italia' : 'TecnoDPI S.r.l.',
      notes: randomBool(rng, 0.45) ? 'Controllo trimestrale registrato' : undefined,
    },
    maskK: {
      condition: mostlyConforme(rng),
      readiness: readinessStatus(rng),
    },
    filtersK: {
      quantity: randomInt(rng, 6, 12),
      type: pick(rng, FILTER_TYPES),
      valence: pick(rng, FILTER_VALENCES),
      capacity: pick(rng, FILTER_CAPACITIES),
      expiry: filterExpiry,
    },
    scba: {
      compliance: mostlyConforme(rng),
      model: rng() > 0.5 ? 'Dräger PSS' : 'Scott Safety',
      quantity: randomInt(rng, 2, 4),
      liters: randomInt(rng, 6, 8),
      ratedPressure: 300,
      measuredPressure: randomInt(rng, 250, 290),
      regulatorType: pick(rng, SCBA_REGULATOR_TYPES),
      inspectionPeriodMonths: randomInt(rng, 6, 12),
      lastService,
      nextCylinderInspection,
    },
    scbaMask: {
      condition: mostlyConforme(rng),
      readiness: readinessStatus(rng),
    },
    suits: {
      type: rng() > 0.4 ? 'stagna' : 'non_stagna',
      quantity: randomInt(rng, 4, 8),
      compliance: mostlyConforme(rng),
    },
    gloves: {
      quantity: randomInt(rng, 6, 12),
      compliance: mostlyConforme(rng),
    },
    boots: {
      quantity: randomInt(rng, 4, 10),
      compliance: mostlyConforme(rng),
    },
    firstAidKit: rng() > 0.8 ? 'non_presente' : pick(rng, FIRST_AID_STATES),
    signage: {
      state: signageState,
      notes: signageState === 'integrare' ? 'Aggiornare indicazioni vie di esodo' : undefined,
    },
    eyeWash: mostlyConforme(rng),
    notes: rng() > 0.5 ? 'Verifica annuale in scadenza' : 'Dotazione completa',
  } satisfies VisitDpiInventory;
}

function buildVisitInailCompliance(rng: Rng, siteName: string): VisitInailCompliance {
  const registered = true;
  const ispeslAntePed = randomBool(rng, 0.35);
  return {
    equipmentType: rng() > 0.5 ? 'Recipienti in pressione' : 'Impianto frigorifero',
    ispeslAntePed,
    registered,
    registrationNotes: registered
      ? `Registro INAIL aggiornato al ${startOfMonth(new Date()).getFullYear()}`
      : undefined,
    periodicInspection: {
      function: randomBool(rng, 0.85),
      integrity: randomBool(rng, 0.8),
    },
    notes: ispeslAntePed
      ? `Verifica ante-PED programmata per ${siteName}`
      : `Documentazione di iscrizione aggiornata per ${siteName}`,
  } satisfies VisitInailCompliance;
}

export function generateSite(
  companyId: string,
  index: number,
  rng: Rng,
  historyLength: number
): GeneratedSite {
  const siteId = newId();
  const now = startOfMonth(new Date());
  const frequency = pick(rng, SITE_FREQUENCIES);
  const frequencyMonths = FREQUENCY_TO_MONTHS.get(frequency) ?? 4;
  const startDate = addMonths(now, -frequencyMonths * Math.max(historyLength - 1, 1));
  const cityInfo = pick(rng, CITY_OPTIONS);
  const sitePurpose = pick(rng, SITE_PURPOSES);
  const address = `${sitePurpose} – ${cityInfo.city}`;
  const addressLine = `Via ${pick(rng, ['Industriale', 'Marconi', 'Europa', 'Meccanica'])}, ${randomInt(rng, 2, 48)}`;
  const cap = String(randomInt(rng, 10000, 98100));
  const siteName = `${sitePurpose} ${index + 1}`;
  const siteLocation = pick(rng, DEFAULT_SITE_LOCATIONS);
  const gasType = pick(rng, DEFAULT_GAS_TYPES);
  const nextVisit = addMonths(now, frequencyMonths);
  const previousVisit = addMonths(now, -frequencyMonths);
  const periodicity = String(frequencyMonths) as Site['periodicity'];
  const plannedPeriod = toIsoDate(nextVisit).slice(0, 7);
  const contractActivationDate = toIsoDate(addMonths(now, -randomInt(rng, 6, 24)));
  const officialGazettePublication = true;
  const gazetteDate = toIsoDate(addMonths(now, -randomInt(rng, 6, 24)));
  const refrigerationUnits = generateRefrigerationUnits(siteId, rng, {
    minUnits: index === 0 ? 2 : undefined,
    plannedPeriod,
  });

  const registers: RegistersSeed = {
    maintenance: {
      status: rng() > 0.2 ? 'Aggiornato' : 'Da aggiornare',
      notes: rng() > 0.6 ? 'Inserire intervento straordinario compressori' : undefined,
    },
    safety: {
      status: rng() > 0.5 ? 'Aggiornato' : 'Archiviazione incompleta',
      notes: rng() > 0.7 ? 'Mancano verbali ultimo trimestre' : undefined,
    },
    charge: {
      status: rng() > 0.5 ? 'Aggiornato' : 'Da aggiornare',
      quantityRecorded: rng() > 0.4 ? `${randomInt(rng, 220, 460)} kg` : undefined,
      notes: rng() > 0.6 ? 'Verificare registrazioni settimanali' : undefined,
    },
  };

  const infrastructureChecks: InfrastructureCheckSeed[] = INFRASTRUCTURE_FIELDS.map(
    (field, idx) => ({
      id: field.id,
      status: rng() > (idx % 3 === 0 ? 0.85 : 0.7) ? 'Conforme' : 'Da verificare',
      notes: rng() > 0.8 ? 'Pianificare manutenzione dedicata' : undefined,
    })
  );

  const scorecardRows: ScorecardSeedRow[] = Array.from(
    { length: Math.max(historyLength, 1) },
    (_unused, rowIndex) => {
      const rowDate = addMonths(startDate, frequencyMonths * rowIndex);
      return {
        id: `${siteId}-score-${rowIndex}`,
        date: toIsoDate(rowDate),
        availability: randomInt(rng, 6, 10),
        interest: randomInt(rng, 6, 10),
        efficiency: randomInt(rng, 6, 10),
        planning: randomInt(rng, 6, 10),
        knowledge: randomInt(rng, 6, 10),
        order: randomInt(rng, 6, 10),
        documentation: randomInt(rng, 6, 10),
        overall: randomInt(rng, 6, 10),
      };
    }
  );

  const emergencyDrillSeed: EmergencyDrillSeed | undefined = randomBool(rng, 0.7)
    ? {
        date: toIsoDate(addMonths(now, -randomInt(rng, 3, 9))),
        scope: rng() > 0.5 ? 'Evacuazione completa' : 'Simulazione perdita ammoniaca',
        method: rng() > 0.5 ? 'Scenario guidato' : 'Esercitazione in tempo reale',
        startTime: `${String(randomInt(rng, 8, 10)).padStart(2, '0')}:00`,
        endTime: `${String(randomInt(rng, 11, 13)).padStart(2, '0')}:30`,
        companyLeads: [`${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`],
        unitResults: refrigerationUnits.map((unit) => ({
          id: newId(),
          unitLabel: unit.name,
          date: toIsoDate(addMonths(now, -randomInt(rng, 1, 6))),
          notes: rng() > 0.6 ? 'Tempo di risposta sotto controllo' : undefined,
        })),
      }
    : undefined;

  const trainingUpdateSeed: TrainingUpdateSeed | undefined = randomBool(rng, 0.65)
    ? {
        executed: rng() > 0.5 ? 'Eseguito' : 'Programmato',
        nextDue: toIsoDate(addMonths(now, randomInt(rng, 4, 8))),
        checks: {
          hazards: rng() > 0.6 ? 'Aggiornato' : 'Da integrare',
          procedures: rng() > 0.6 ? 'Aggiornato' : 'Da integrare',
          info: 'Distribuite dispense aggiornate',
          dvrUpdate: rng() > 0.5 ? 'Verificato' : 'Da pianificare',
          dpiUpdate: rng() > 0.5 ? 'Verificato' : 'Da pianificare',
        },
        participants: shuffle(rng, [
          `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`,
          `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`,
          `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`,
        ])
          .slice(0, randomInt(rng, 2, 3))
          .map((name) => ({
            id: newId(),
            name,
            notes: rng() > 0.7 ? 'Preso nota delle procedure' : undefined,
          })),
      }
    : undefined;

  const simulationSeed: SimulationSeed | undefined = randomBool(rng, 0.45)
    ? {
        mode: rng() > 0.5 ? 'Reale' : 'In aula',
        position: rng() > 0.5 ? 'Locale compressori' : 'Area picking',
        stairsNeeded: rng() > 0.5,
        notes: rng() > 0.6 ? 'Esercitazione congiunta VV.FF.' : undefined,
        participants: shuffle(rng, [
          `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`,
          `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`,
        ]).map((name) => ({
          id: newId(),
          name,
          prepTime: `${randomInt(rng, 4, 7)} min`,
          interventionTime: `${randomInt(rng, 6, 12)} min`,
          pressureStart: `${randomInt(rng, 180, 220)} bar`,
          pressureEnd: `${randomInt(rng, 90, 140)} bar`,
          consumption: `${randomInt(rng, 3, 6)} l`,
          conform: rng() > 0.6 ? 'Conforme' : 'Da ottimizzare',
        })),
        tracts: refrigerationUnits.map((unit) => ({
          id: newId(),
          description: `Verifica ${unit.name}`,
        })),
        difficulties: rng() > 0.7 ? ['Coordinamento evacuazione'] : [],
        suggestions: rng() > 0.7 ? ['Ripetere prova con turni notturni'] : [],
      }
    : undefined;

  const safetySummary: SafetySummarySeed = {
    authorizationCompliance: 'Conforme',
    activeSensors: `${randomInt(rng, 4, 6)} sensori attivi`,
    calibrationSummary: 'Taratura semestrale registrata',
    valvesSummary: rng() > 0.5 ? 'Verifica programmata' : 'Verifica completata',
  };

  const unitChecks = buildVisitUnitChecks(refrigerationUnits, rng, now);
  const detailedChecks = buildVisitDetailedChecks(refrigerationUnits, rng);
  const dpiInventory = buildVisitDpiInventory(rng, now);
  const inailCompliance = buildVisitInailCompliance(rng, siteName);
  const authorizedNh3Kg = refrigerationUnits
    .map((unit) => unit.ammoniaKg ?? 0)
    .reduce((sum, value) => sum + value, 0);

  const visitSeed: VisitSeedData = {
    infrastructureChecks,
    registers,
    safetySummary,
    unitChecks,
    detailedChecks,
    dpiInventory,
    inailCompliance,
    emergencyDrill: emergencyDrillSeed,
    trainingUpdate: trainingUpdateSeed,
    simulation: simulationSeed,
    scorecard: scorecardRows,
  };

  const site: Site = {
    id: siteId,
    companyId,
    name: siteName,
    sequenceNumber: index + 1,
    address,
    sedeOperativa: `${addressLine}, ${cap} ${cityInfo.city} (${cityInfo.province})`,
    sedeOperativaAddress: addressLine,
    sedeOperativaCap: cap,
    sedeOperativaCity: cityInfo.city,
    sedeOperativaProvince: cityInfo.province,
    contactEmail: undefined,
    contactPhone: undefined,
    frequency,
    gasType,
    notes: undefined,
    periodicityControlPeriod: formatControlPeriodRange(previousVisit, frequencyMonths),
    periodicityPreviousVisit: toIsoDate(previousVisit),
    periodicityNextVisit: toIsoDate(nextVisit),
    periodicityNextVisitAt: toIsoDateTime(applyWorkingHours(nextVisit, rng)),
    refrigerationUnitsCount: refrigerationUnits.length,
    siteType: pick(rng, [
      'Refrigerazione industriale',
      'Logistica del freddo',
      'Conservazione prodotti freschi',
    ]),
    siteLocation: siteLocation,
    toxicGas: gasType,
    authorizationProtocolNumber: `PRT-${randomInt(rng, 1000, 9999)}`,
    authorizationIssueDate: toIsoDate(addMonths(now, -randomInt(rng, 24, 48))),
    authorizationExpiryDate: toIsoDate(addMonths(now, randomInt(rng, 12, 24))),
    authorizedNh3Kg,
    officialGazettePublished: officialGazettePublication,
    officialGazetteDate: gazetteDate,
    cpiPresent: true,
    cpiProtocolNumber: `CPI-${randomInt(rng, 1000, 9999)}`,
    cpiLastRenewalDate: toIsoDate(addMonths(now, -randomInt(rng, 6, 18))),
    cpiExpiryDate: toIsoDate(addMonths(now, randomInt(rng, 12, 24))),
    ammoniaActivityCodes: true,
    siteComponents: [],
    periodicity,
    contractActivationDate,
    refrigerationUnits,
    btGreaseBandPresent: randomBool(rng, 0.3),
    maintenanceNotes: rng() > 0.6 ? 'Prossimo intervento programmato' : undefined,
    createdAt: startOfMonth(addMonths(now, -randomInt(rng, 3, 12))).toISOString(),
    updatedAt: now.toISOString(),
  } satisfies Site;

  const { refrigerationUnits: _refrigerationUnits, refrigerationUnitsCount, ...siteInput } = site;
  void _refrigerationUnits;
  void refrigerationUnitsCount;
  const input: SiteInput = siteInput;

  return {
    site,
    input,
    frequencyMonths,
    startDate,
    visitSeed,
    issues: new Map<string, SiteIssueRecord>(),
    nextIssueIndex: 1,
  };
}
